// Transform booking data from backend (camelCase) to frontend (snake_case) format
export const transformBookingToFrontend = (booking) => ({
  id: booking.id,
  patient_id: booking.patientIdentificationNumber || booking.patientId, // Show SA ID number in UI
  patient_database_id: booking.patientId, // Store database ID separately
  patient_email: booking.patientEmail,
  doctor_id: booking.doctorId,
  service_id: booking.serviceId,
  room_id: booking.roomId,
  date: booking.date,
  start_time: booking.startTime,
  end_time: booking.endTime,
  status: booking.status,
  total_price: booking.totalPrice,
  notes: booking.notes,
  created_at: booking.createdAt,
  updated_at: booking.updatedAt,
  practice_id: booking.practiceId,
  // For compatibility with existing calendar components
  appointment_date: `${booking.date}T${booking.startTime}:00`,
  provider_name: booking.doctorId, // Use ID, resolve name in components
  // Telemedicine fields (pass through as-is)
  isTelemedicine: booking.isTelemedicine,
  zoomMeeting: booking.zoomMeeting,
  zoomStatus: booking.zoomStatus,
  zoomError: booking.zoomError,
});

// Transform booking data from frontend (snake_case) to backend (camelCase) format
export const transformBookingToBackend = (booking) => {
  const transformed = {
    patientEmail: booking.patient_email,
    patientIdentificationNumber: booking.patient_id, // SA ID number for verification
    doctorId: booking.doctor_id,
    serviceId: booking.service_id,
    roomId: booking.room_id,
    date: booking.date,
    startTime: booking.start_time,
    endTime: booking.end_time,
    status: booking.status || 'scheduled',
    totalPrice: booking.total_price,
    notes: booking.notes,
  };

  // Only include practiceId if it exists
  if (booking.practice_id) {
    transformed.practiceId = booking.practice_id;
  }

  // Only include patientId if it's actually a database ID (not SA ID number)
  if (booking.patient_database_id) {
    transformed.patientId = booking.patient_database_id;
  }

  return transformed;
};

// Transform API response with timezone information
export const transformTimezoneResponse = (response) => {
  if (response.timezone) {
    return {
      ...response,
      timezone: {
        identifier: response.timezone.identifier,
        displayNote: response.timezone.displayNote,
        abbreviation: response.timezone.abbreviation || null
      }
    };
  }
  return response;
};

// Transform availability data with timezone context
export const transformAvailabilityWithTimezone = (availabilityData, timezone) => {
  if (!availabilityData || !timezone) return availabilityData;

  return {
    ...availabilityData,
    dates: availabilityData.dates || [],
    availability: (availabilityData.availability || []).map(item => ({
      ...item,
      timezone_context: {
        identifier: timezone.identifier,
        displayNote: timezone.displayNote
      }
    })),
    timezone
  };
};

// Transform slots data with timezone context
export const transformSlotsWithTimezone = (slotsData, timezone) => {
  if (!slotsData || !timezone) return slotsData;

  const slots = Array.isArray(slotsData) ? slotsData : slotsData.slots || [];
  
  return {
    slots: slots.map(slot => ({
      ...slot,
      timezone_context: {
        identifier: timezone.identifier,
        displayNote: timezone.displayNote
      }
    })),
    timezone
  };
};

// Transform service data from backend (camelCase) to frontend (snake_case) format
export const transformServiceToFrontend = (service) => ({
  id: service.id,
  name: service.name,
  description: service.description,
  category: service.category,
  subcategory: service.subcategory,
  total_duration_minutes: service.totalDurationMinutes,
  preparation_time: service.preparationTime,
  requires_doctor: service.requiresDoctor,
  requires_room: service.requiresRoom,
  status: service.status,
  equipment_required: service.equipmentRequired,
  contraindications: service.contraindications,
  side_effects: service.sideEffects,
  pre_service_requirements: service.preServiceRequirements,
  post_service_care: service.postServiceCare,
  requires_lab_work: service.requiresLabWork,
  consent_form_required: service.consentFormRequired,
  rooms_required: service.roomsRequired,
  base_price: service.basePrice,
  created_at: service.createdAt,
  updated_at: service.updatedAt,
  created_by: service.createdBy,
  practice_id: service.practiceId,
  doctors_assigned: service.doctors_assigned, // Already snake_case
  rooms_assigned: service.rooms_assigned, // Already snake_case
  equipment_assigned: service.equipment_assigned, // Already snake_case
});

// Transform service data from frontend (snake_case) to backend (camelCase) format
export const transformServiceToBackend = (service) => ({
  id: service.id,
  name: service.service_name,
  serviceCode: service.service_code,
  description: service.description,
  category: service.category,
  subcategory: service.subcategory,
  totalDurationMinutes: service.duration_minutes,
  preparationTime: service.preparation_time,
  requiresDoctor: service.requires_doctor,
  requiresRoom: service.requires_room !== undefined ? service.requires_room : true,
  status: service.status,
  equipmentRequired: service.equipment_required,
  contraindications: service.contraindications,
  sideEffects: service.side_effects,
  preServiceRequirements: service.pre_service_requirements,
  postServiceCare: service.post_service_care,
  requiresLabWork: service.requires_lab_work,
  consentFormRequired: service.consent_form_required,
  roomsRequired: service.rooms_required,
  basePrice: service.base_price,
  frequencyRecommendations: service.frequency_recommendations,
  notes: service.notes,
  doctorsAssigned: service.doctors_assigned,
  roomsAssigned: service.rooms_assigned,
  equipmentAssigned: service.equipment_assigned,
});

